import { Link } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import Breadcrumb from '../../components/layout/Breadcrumb';
import { getFaqForRoute } from '../../data/faq';
import { relatedSites } from '../../data/related';
import { NavyBand } from './_detail';

// ===== 関連サイト ハブ(一覧)共通描画(2026-09-18 作り直し) =====
// 導入(協会から見た関連事業の全体像)→ 4 サイトのカード(実写)→ つながりの説明 → 学び・発信・実務・体験の動線 → FAQ(faq.json 正本)→ CTA。
// デザインは当協会サイトの流儀(NavyBand・紺#1a2942・真鍮金#a38e65・BTN-08)。外部公式 URL は新規タブ + rel="noopener noreferrer"。

const BTN08_ON_NAVY = "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";
const WRAP = 'max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10';
const P = "font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#333] leading-[1.95]";

const FLOW = {
  ja: [
    { lv: '1', tag: 'Learn', title: '知る — 執事ベル', desc: '執事とは何か、執事の知恵、協会が統括する事業を、頭取・石井裕一が動画・記事・著書で伝えます。執事の世界への最初の入口です。', slug: 'butler-bell' },
    { lv: '2', tag: 'Study', title: '学ぶ — 日本執事アカデミー', desc: '協会の基準をそのままカリキュラムにした三つのコース。修了と実技試験の合格をもって、協会認定資格が授与されます。', slug: 'butler-academy' },
    { lv: '3', tag: 'Engage', title: '仕えてもらう — THE BUTLER', desc: '協会の基準で育った執事が、ご家庭と法人にお仕えします。執事名簿から、お客様ご自身で執事をお選びいただけます。', slug: 'the-butler' },
    { lv: '4', tag: 'Experience', title: '体験する — Queen\'s Butler', desc: 'お台場の館で、執事に仕えられる時間そのものを体験する女性のための執事サロン。スタジオや執事見習い体験もあります。', slug: 'queens-butler' },
  ],
  en: [
    { lv: '1', tag: 'Learn', title: 'Learn — Butler Bell', desc: 'What a butler is, the butler\'s wisdom and the businesses the Association oversees, told by its head, Yuichi Ishii, through video, articles and books. The first entry point to the world of butlers.', slug: 'butler-bell' },
    { lv: '2', tag: 'Study', title: 'Study — Japan Butler Academy', desc: 'Three courses whose curriculum is the Association\'s standard itself. Completion and a pass in the practical examination lead to Association certification.', slug: 'butler-academy' },
    { lv: '3', tag: 'Engage', title: 'Be served — THE BUTLER', desc: 'Butlers trained to the Association\'s standard serve households and companies. Clients choose their own butler from the register.', slug: 'the-butler' },
    { lv: '4', tag: 'Experience', title: 'Experience — Queen\'s Butler', desc: 'A butler salon for women in a house in Odaiba, where the time of being served itself is the experience, with a studio and a butler apprentice experience alongside.', slug: 'queens-butler' },
  ],
};

const FLOW_IMG: Record<string, string> = {
  'butler-bell': '/images/related/butler-bell/typewriter.jpg',
  'butler-academy': '/images/related/butler-academy/seminar.jpg',
  'the-butler': '/images/related/the-butler/service.jpg',
  'queens-butler': '/images/related/queens-butler/tea.jpg',
};

// 協会が保つ四つの約束(章 IV)。画像は各紹介先の公式リポが現に参照しているものを無加工コピーしたもの。
const PROMISES = {
  ja: [
    { en: 'Standard', title: '執事の標準を定める', desc: '礼節・守秘・立ち居振る舞いを章立てで文章にし、何をもって執事と呼ぶのかを公開しています。基準が言葉になっていなければ、教えることも確かめることもできません。', img: '/images/related/the-butler/standard.jpg', alt: '協会の基準に沿った執事の装い' },
    { en: 'Certification', title: '認定を行う', desc: 'アソシエイト・プロフェッショナル・マスターの三段階で、どこまで任せられるかを本人とお客様の双方に分かる形にします。認定は受講した証ではなく、できる証です。', img: '/images/related/butler-academy/instructor2.jpg', alt: '現場の執事が実技を指導する様子' },
    { en: 'Register', title: '名簿を公開する', desc: '在籍する執事の経歴と得意分野を公開し、お客様ご自身で執事をお選びいただけるようにしています。名前が表に出ることは、執事にとって健全な緊張になります。', img: '/images/related/butler-bell/gramophone.jpg', alt: '円卓を囲む受講者にテーブルマナーを説く執事' },
    { en: 'Discretion', title: '守秘を徹底する', desc: '見聞きしたことを外へ持ち出さないことは、執事の技能以前の前提です。個人のお客様でも法人でも、また体験の場であっても、同じ原則が等しく適用されます。', img: '/images/related/queens-butler/twobow.jpg', alt: '並んで一礼する二人の執事' },
  ],
  en: [
    { en: 'Standard', title: 'Setting the standard', desc: 'Courtesy, confidentiality and deportment are set down in chapters, and what makes a butler is published openly. A standard that has not been put into words can be neither taught nor verified.', img: '/images/related/the-butler/standard.jpg', alt: 'Butler attire that follows the standard' },
    { en: 'Certification', title: 'Granting certification', desc: 'Three tiers — Associate, Professional, Master — make the range of what may be entrusted clear to the butler and the client alike. Certification is proof of ability, not of attendance.', img: '/images/related/butler-academy/instructor2.jpg', alt: 'A working butler teaching a practical class' },
    { en: 'Register', title: 'Publishing the register', desc: 'The career and strengths of every serving butler are published so that clients may choose for themselves. Having one\'s name in the open is a healthy pressure for a butler.', img: '/images/related/butler-bell/gramophone.jpg', alt: 'A butler explaining table manners around a round table' },
    { en: 'Discretion', title: 'Holding to discretion', desc: 'Never carrying outside what is seen or heard comes before any skill. The same rule applies equally to private clients, to companies and to the house where the work is experienced.', img: '/images/related/queens-butler/twobow.jpg', alt: 'Two butlers bowing side by side' },
  ],
};

export default function RelatedHub({ lang }: { lang: 'ja' | 'en' }) {
  const isEn = lang === 'en';
  const t = (ja: string, en: string) => (isEn ? en : ja);
  const base = isEn ? '/en/related' : '/related';
  const faqs = getFaqForRoute(base);
  const breadcrumbs = [{ label: t('ホーム', 'Home'), href: isEn ? '/en/' : '/' }, { label: t('関連サイト', 'Related Sites') }];

  return (
    <Layout>
      {/* ===== ヒーロー — 全幅ビルボード型 ===== */}
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[480px] lg:min-h-[540px] flex items-center">
        <img src="/images/related/the-butler/hero.jpg" alt={t('邸宅で控える正装の執事(THE BUTLER)', 'A butler in formal dress standing by in a residence (THE BUTLER)')} className="absolute inset-0 w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.92)_0%,rgba(16,20,26,0.7)_50%,rgba(16,20,26,0.4)_100%)]"></div>
        <div className={`relative z-10 w-full ${WRAP} pt-32 pb-16 lg:pt-36 lg:pb-20`}>
          <div className="max-w-[720px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">Related Organizations</span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(26px,3.2vw,42px)] leading-[1.42] tracking-[0.04em] mb-6">
              <span className="block">{t('執事文化を支える、', 'The organizations that')}</span>
              <span className="block">{t('協会グループのご案内', 'sustain butler culture')}</span>
            </h1>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
              {t(
                '一般社団法人全日本執事協会は、執事の基準と認定を担う母体です。その基準のもとで、執事文化を伝える発信、執事を育てる学び、実際にお仕えするサービス、仕えられる時間を体験する館が、一つの流れとしてつながっています。',
                'The Japan Butler Association sets and certifies the butler\'s standard. Under it, a media brand, a school, a butler service and a house where one is served are connected as a single flow.',
              )}
            </p>
          </div>
        </div>
      </section>
      {/* パンくず: ビルボード直後に配置（Yuichi 2026-07-17 指摘②）。共通Breadcrumbのヘッダー逃げpaddingを子セレクタで通常paddingへ上書き */}
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      {/* ===== I. 協会グループの全体像 ===== */}
      <NavyBand no="I" ja={t('協会グループの全体像', 'Our network at a glance')} en="Our Network" />
      <section className="py-12 sm:py-16 bg-white">
        <div className={WRAP}>
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div className="order-2 lg:order-1 text-left">
              <h3 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[30px] leading-[1.5] tracking-[0.02em] mb-5">
                {t('基準を定める協会を中心に、', 'Around the body that sets the standard, ')}<span className="text-[#a38e65]">{t('学びから実務まで', 'from learning to practice')}</span>
              </h3>
              <p className={`${P} mb-4`}>
                {t(
                  '1890 年の創立以来、当協会は執事の育成・認定と、日本の執事文化の標準を定める役割を担ってきました。2025 年に一般社団法人として体制を整え、資格制度・法人研修・執事名簿の公開を通じて、執事という職業の質を保つ母体として活動しています。',
                  'Since 1890 the Association has trained and certified butlers and set the standard for butler culture in Japan. Incorporated in 2025, it maintains the quality of the profession through certification, corporate training and a published register.',
                )}
              </p>
              <div className="mt-6 border-t border-[#e2ddd2]">
                {[
                  {
                    t: t('基準と現場を往復させる', 'Standard and field, back and forth'),
                    b: t(
                      '基準を定める団体が、その基準で動く現場を持っていることには意味があります。現場で起きたことは基準の見直しへ戻り、改められた基準はまた現場へ降りていく。この往復が止まると、基準は文書のまま古びていきます。',
                      'It matters that the body setting the standard also holds the places where that standard is worked. What happens in the field returns to the review of the standard, and the revised standard goes back out. When that circuit stops, a standard grows old on the page.',
                    ),
                  },
                  {
                    t: t('四つの役割は重なっていない', 'Four roles that do not overlap'),
                    b: t(
                      '伝えること、教えること、お仕えすること、体験していただくこと。関心の持ち方によって最初に触れる場所は変わりますが、たどり着く執事は同じ基準で育った者です。',
                      'To convey, to teach, to serve, and to let the work be experienced. Where a person first arrives depends on what draws them, but the butlers they reach were all raised to the same standard.',
                    ),
                  },
                  {
                    t: t('このページでご紹介すること', 'What this page introduces'),
                    b: t(
                      '頭取自らが執事文化を社会へ伝える執事ベル、協会の基準をカリキュラムにした日本執事アカデミー、認定を受けた執事がお仕えする THE BUTLER、そして仕えられる時間を体験できる Queen\'s Butler。四つの関連事業について、それぞれの役割と当協会とのつながりをご紹介します。',
                      'Butler Bell for what is conveyed, the Japan Butler Academy for what is taught, THE BUTLER for what is served, and Queen\'s Butler for what is experienced. This page introduces the four and their connection with us.',
                    ),
                  },
                ].map((s) => (
                  <div key={s.t} className="border-b border-[#e2ddd2] py-4">
                    <h4 className="jba-display font-medium text-[#1a2942] text-lg mb-2">{s.t}</h4>
                    <p className="font-['Noto_Serif_JP',serif] text-sm sm:text-base text-[#4a4638] leading-[1.9]">{s.b}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <img src="/images/related/queens-butler/wide.jpg" alt={t('Queen\'s Butler 正餐の間の全景', 'The dining room of Queen\'s Butler')} className="w-full h-[300px] sm:h-[400px] md:h-[460px] object-cover object-top border border-[#a38e65]/30" />
            </div>
          </div>
        </div>
      </section>

      {/* ===== II. 四つの関連サイト — 実写カード ===== */}
      <NavyBand no="II" ja={t('四つの関連サイト', 'Four related sites')} en="Related Sites" />
      <section className="py-12 sm:py-16 bg-white">
        <div className={WRAP}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
            {relatedSites.map((s) => {
              const c = isEn ? s.en : s.ja;
              return (
                <article key={s.slug} className="group flex flex-col bg-white border border-[#e2ddd2] hover:border-[#a38e65]/70 transition-colors duration-300">
                  <div className="relative aspect-[16/9] overflow-hidden">
                    <img src={c.hub.src} alt={c.hub.alt} className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: c.hub.pos ?? 'top' }} />
                    <span className="absolute top-4 left-4 bg-[#1a2942]/90 text-[#d7b779] text-[11px] tracking-[0.08em] px-3 py-1 border border-[#a38e65]/40">{c.role}</span>
                  </div>
                  <div className="flex flex-col flex-1 p-7 sm:p-8 text-left">
                    <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-sm tracking-[0.06em] mb-2">{c.roleEn}</span>
                    <h3 className="jba-display font-medium text-[#1a2942] text-xl sm:text-2xl leading-[1.4] mb-4">{c.name}</h3>
                    <p className="font-['Noto_Serif_JP',serif] text-sm sm:text-base text-[#4a4638] leading-[1.9] mb-6 flex-1">{c.tagline}</p>
                    <div className="flex flex-col gap-3 pt-5 border-t border-[#e2ddd2]">
                      <Link to={`${base}/${s.slug}${isEn ? '/' : ''}`} className="inline-flex items-center gap-2 font-['Noto_Serif_JP',serif] text-[15px] text-[#1a2942] tracking-[0.04em] border-b border-[#a38e65] pb-1 self-start hover:text-[#8f7a56] transition-colors duration-300">
                        {t('詳しく見る', 'Learn more')} <span className="text-[#a38e65] group-hover:translate-x-1 transition-transform">→</span>
                      </Link>
                      <a href={s.officialUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] hover:text-[#a38e65] transition-colors self-start">
                        {t('公式サイト', 'Official site')} <i className="ri-external-link-line text-xs"></i>
                      </a>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== III. 協会と関連サイトのつながり — 紺パネル ===== */}
      <NavyBand no="III" ja={t('協会と関連サイトのつながり', 'How we are connected')} en="Relationship" />
      <section className="py-12 sm:py-16 bg-white">
        <div className={WRAP}>
          <div className="grid lg:grid-cols-2 items-stretch border border-[#e2ddd2] overflow-hidden">
            <div className="relative min-h-[300px] lg:min-h-[440px] bg-black order-1">
              <img src="/images/related/butler-academy/hero.jpg" alt={t('日本執事アカデミーの執事たち', 'Butlers of the Japan Butler Academy')} className="absolute inset-0 w-full h-full object-cover object-top" />
            </div>
            <div className="order-2 bg-[#1a2942] flex flex-col justify-center px-8 sm:px-12 lg:px-14 py-12 lg:py-16 text-left">
              <span className="block font-['Playfair_Display',serif] italic text-[#a38e65] text-sm tracking-[0.06em] mb-4">How We Are Connected</span>
              <h3 className="jba-display font-medium text-[#f8f6f0] text-2xl sm:text-3xl leading-[1.5] tracking-[0.02em] mb-6">
                {t('一つの協会のもとで、', 'Under one Association, ')}<span className="text-[#d7b779]">{t('つながっています', 'all connected')}</span>
              </h3>
              <p className="font-['Noto_Serif_JP',serif] text-base text-[#e7e2d8] leading-[1.95] mb-4">
                {t(
                  'THE BUTLER、日本執事アカデミー、Queen\'s Butler は、当協会が運営する事業・機関です。執事ベルは当協会 頭取・石井裕一のオフィシャル情報発信ブランドです。学びと発信と実務が、一つの協会のもとでつながっています。',
                  'THE BUTLER, the Japan Butler Academy and Queen\'s Butler are businesses and institutions operated by the Association. Butler Bell is the official media brand of its head, Yuichi Ishii. Learning, communication and practice are connected under one Association.',
                )}
              </p>
              <h4 className="jba-display font-medium text-[#d7b779] text-lg mt-2 mb-2">{t('どの入口でも、同じ基準へ', 'Every entrance, one standard')}</h4>
              <p className="font-['Noto_Serif_JP',serif] text-base text-[#e7e2d8] leading-[1.95]">
                {t(
                  '当協会は、いずれの事業にも共通する「執事の標準」を定め、認定を行います。どの入口から入っても、同じ基準で育った執事にたどり着く――それが協会グループの約束です。',
                  'The Association sets the Butler Standards common to all of these and carries out certification. Whichever entrance you take, you arrive at butlers trained to the same standard — that is the group\'s promise.',
                )}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== IV. 協会が保つ四つの約束 ===== */}
      <NavyBand no="IV" ja={t('協会が保つ四つの約束', 'Four promises we keep')} en="Our Promises" />
      <section className="py-12 sm:py-16 bg-white">
        <div className={WRAP}>
          <p className={`${P} max-w-4xl mb-10 text-left`}>
            {t(
              '四つの関連サイトは、事業としては別々に運営されています。それでも同じ質を保てるのは、当協会が全てに共通して負っている約束があるからです。以下の四点は、どの事業のどの現場にも等しく適用されます。',
              'The four related sites are run as separate businesses. They hold the same quality because the Association carries promises common to them all. The four below apply equally to every one of these businesses and to every occasion of service.',
            )}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-7 lg:gap-8">
            {PROMISES[lang].map((p) => (
              <article key={p.en} className="flex flex-col bg-white border border-[#e2ddd2] hover:border-[#a38e65]/70 transition-colors duration-300">
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img src={p.img} alt={p.alt} loading="lazy" className="absolute inset-0 w-full h-full object-cover object-top" />
                </div>
                <div className="flex flex-col flex-1 p-6 sm:p-7 text-left">
                  <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-[13px] tracking-[0.06em] mb-2">{p.en}</span>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl leading-[1.45] mb-3">{p.title}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm sm:text-base text-[#4a4638] leading-[1.9]">{p.desc}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ===== V. 知る・学ぶ・仕えてもらう・体験する ===== */}
      <NavyBand no="V" ja={t('知る・学ぶ・仕えてもらう・体験する', 'Learn, study, be served, experience')} en="Your Path" />
      <section className="py-12 sm:py-16 bg-white">
        <div className={WRAP}>
          <p className={`${P} max-w-4xl mb-10 text-left`}>
            {t(
              '執事に関心をお持ちの方が、どこから入り、どこへ進めばよいか。当協会の立場から、四つの関連サイトを一つの動線として整理しました。',
              'Where should someone interested in butlers begin, and where should they go next? From the Association\'s standpoint, the four related sites form a single path.',
            )}
          </p>
          <h3 className="jba-display font-medium text-[#1a2942] text-xl sm:text-2xl leading-[1.5] mb-3 text-left">{t('順に辿らなくて構いません', 'You need not follow them in order')}</h3>
          <p className={`${P} max-w-4xl mb-10 text-left`}>
            {t(
              '順に辿っていただく必要はありません。読み物から入る方、いきなり依頼される方、まず体験してみたい方。どこから入っても構いませんが、次に進みたくなったときの行き先が分かるように、四つを一続きの道として並べています。',
              'There is no need to follow them in order. Some begin by reading, some engage a butler straight away, some wish to experience the work first. Any entrance will do; the four are laid out as one road so that the next destination is clear when you want it.',
            )}
          </p>
          <div className="border-t border-[#e2ddd2]">
            {FLOW[lang].map((f) => (
              <Link key={f.slug} to={`${base}/${f.slug}${isEn ? '/' : ''}`} className="group grid grid-cols-[auto_1fr] md:grid-cols-[72px_200px_1fr] gap-x-6 gap-y-3 items-center border-b border-[#e2ddd2] py-6 text-left hover:bg-[#f8f6f0] transition-colors">
                <span className="font-['Playfair_Display',serif] text-[#a38e65] text-3xl leading-none">{f.lv}</span>
                <div className="h-40 md:h-32 w-full max-w-[200px]">
                  <img src={FLOW_IMG[f.slug]} alt="" loading="lazy" className="w-full h-full object-cover object-top border border-[#a38e65]/30" />
                </div>
                <div className="col-span-2 md:col-span-1">
                  <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-[13px] tracking-[0.06em] mb-1">{f.tag}</span>
                  <h4 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl mb-2 leading-[1.4] group-hover:text-[#8f7a56] transition-colors">{f.title}</h4>
                  <p className="font-['Noto_Serif_JP',serif] text-sm sm:text-base text-[#4a4638] leading-[1.9]">{f.desc}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ===== V. よくあるご質問（faq.json 正本・自前描画）===== */}
      <NavyBand no="VI" ja={t('よくあるご質問', 'Frequently asked questions')} en="FAQ" />
      <section className="py-12 sm:py-16 bg-white">
        <div className={WRAP}>
          <div className="max-w-4xl mx-auto border-t border-[#e2ddd2]">
            {faqs.map((f) => (
              <div key={f.q} className="border-b border-[#e2ddd2] py-6 text-left">
                <h3 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl leading-[1.5] mb-3 flex items-start gap-3">
                  <span className="font-['Playfair_Display',serif] text-[#a38e65] text-xl leading-none pt-0.5">Q.</span><span>{f.q}</span>
                </h3>
                <p className="font-['Noto_Serif_JP',serif] text-sm sm:text-base text-[#4a4638] leading-[1.95] pl-8 whitespace-pre-line">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 締めCTAパネル ===== */}
      <section className="py-12 sm:py-16 bg-white">
        <div className={WRAP}>
          <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 md:p-14 text-center">
            <h3 className="jba-display font-medium text-2xl sm:text-3xl text-[#f8f6f0] tracking-[0.04em]">{t('執事の学びや資格に関心をお持ちの方へ', 'Interested in butler training or certification?')}</h3>
            <div className="w-11 h-px bg-[#a38e65] mx-auto mt-4 mb-6"></div>
            <p className="text-base sm:text-lg text-[#e7e2d8] mb-8 font-['Noto_Serif_JP',serif] max-w-2xl mx-auto leading-[1.9]">{t('執事資格制度や法人研修、各サービスについて、ご不明な点はお気軽にお問い合わせください。', 'For questions about the certification system, corporate training or any of the services, please feel free to contact us.')}</p>
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center">
              <Link to={isEn ? '/en/qualifications/' : '/qualifications'} className={BTN08_ON_NAVY}>{t('執事資格制度を見る', 'Certification System')}</Link>
              <Link to={isEn ? '/en/contact/' : '/contact'} className={BTN08_ON_NAVY}>{t('お問い合わせ', 'Contact Us')}</Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
